import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DownloadSimple,
  Eye,
  FileText,
  SpinnerGap,
  X
} from '@phosphor-icons/react';
import { resolvePreviewType } from '../../utils/filePreview.js';

const MAX_SHEET_ROWS = 1000;
const PREVIEW_ERROR_MESSAGE = 'No se pudo cargar la vista previa del documento.';

const INITIAL_PREVIEW_STATE = {
  type: '',
  text: '',
  workbook: null
};

const fetchPreviewResponse = async (url) => {
  const response = await fetch(url, { method: 'GET' });

  if (!response.ok) {
    throw new Error(`${PREVIEW_ERROR_MESSAGE} Codigo: ${response.status}`);
  }

  return response;
};

// Cada hoja se pasa a filas de strings ya formateados (raw: false) para
// renderizarla como tabla React — sin sheet_to_html/dangerouslySetInnerHTML.
const buildWorkbookPreview = (XLSX, buffer) => {
  const workbook = XLSX.read(buffer, { type: 'array' });

  return workbook.SheetNames.map((sheetName) => {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      blankrows: false,
      defval: '',
      raw: false
    });
    const visibleRows = rows.slice(0, MAX_SHEET_ROWS);
    const columnCount = visibleRows.reduce((max, row) => Math.max(max, row.length), 0);

    return {
      name: sheetName,
      rows: visibleRows,
      columnCount,
      totalRows: rows.length
    };
  });
};

/**
 * Visor genérico de archivos (imagen, Word, Excel, texto).
 * - inlineUrl: URL servida con Content-Disposition inline, usada directo en <img>.
 * - fetchUrl: URL del binario, se pide con fetch para docx-preview / SheetJS / texto.
 * Los PDF no pasan por acá: el caller los abre en pestaña nueva.
 */
function FilePreviewModal({ isOpen, file, inlineUrl, fetchUrl, onDownload, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewState, setPreviewState] = useState(INITIAL_PREVIEW_STATE);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const docxContainerRef = useRef(null);

  const modalTarget = useMemo(() => (
    typeof window !== 'undefined' ? window.document.body : null
  ), []);

  useEffect(() => {
    if (!isOpen || !file) {
      return undefined;
    }

    let cancelled = false;
    const previewType = resolvePreviewType(file);
    const docxContainer = docxContainerRef.current;

    const loadPreview = async () => {
      setError(null);
      setDownloadError(null);
      setActiveSheetIndex(0);
      setPreviewState({ ...INITIAL_PREVIEW_STATE, type: previewType });

      if (previewType === 'image' || previewType === 'unsupported') {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const response = await fetchPreviewResponse(fetchUrl);

        if (previewType === 'text') {
          const text = await response.text();
          if (!cancelled) {
            setPreviewState({ ...INITIAL_PREVIEW_STATE, type: 'text', text });
          }
          return;
        }

        const buffer = await response.arrayBuffer();
        if (cancelled) {
          return;
        }

        if (previewType === 'docx') {
          const { renderAsync } = await import('docx-preview');
          if (cancelled || !docxContainer) {
            return;
          }

          docxContainer.innerHTML = '';
          await renderAsync(buffer, docxContainer, undefined, {
            inWrapper: true,
            ignoreLastRenderedPageBreak: true
          });
          return;
        }

        if (previewType === 'sheet') {
          const XLSX = await import('xlsx');
          const workbook = buildWorkbookPreview(XLSX, buffer);
          if (!cancelled) {
            setPreviewState({ ...INITIAL_PREVIEW_STATE, type: 'sheet', workbook });
          }
        }
      } catch (previewError) {
        if (!cancelled) {
          setError(previewError.message || PREVIEW_ERROR_MESSAGE);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;

      if (docxContainer) {
        docxContainer.innerHTML = '';
      }
    };
  }, [file, fetchUrl, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setPreviewState(INITIAL_PREVIEW_STATE);
      setError(null);
      setDownloadError(null);
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.document.addEventListener('keydown', handleEscape);
    return () => window.document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !file || !modalTarget) {
    return null;
  }

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);

    try {
      await onDownload();
    } catch (downloadErr) {
      setDownloadError(downloadErr.message || 'No se pudo descargar el archivo.');
    } finally {
      setDownloading(false);
    }
  };

  const activeSheet = previewState.workbook?.[activeSheetIndex] || null;
  const showContent = !loading && !error;

  return createPortal(
    <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/70 p-4">
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative flex max-h-full w-fit max-w-6xl flex-col overflow-hidden rounded-3xl border border-line bg-bg shadow-2xl sm:min-w-[28rem]">
        <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-accent-text">
              <Eye size={14} />
              Vista previa
            </div>
            <h3 className="mt-1 truncate text-lg font-semibold text-content">{file.name}</h3>
            {downloadError && (
              <p className="mt-1 text-sm text-red-300">{downloadError}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-2 rounded-xl border border-amber/40 bg-[--app-sidebar-active] px-4 py-2 text-sm font-medium text-accent-text transition-colors hover:bg-amber/20 hover:text-amber-hover disabled:cursor-not-allowed disabled:opacity-70"
            >
              {downloading ? <SpinnerGap size={16} className="animate-spin" /> : <DownloadSimple size={16} />}
              {downloading ? 'Descargando...' : 'Descargar'}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-surface text-content transition-colors hover:bg-surface-2 hover:text-content"
              aria-label="Cerrar vista previa"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-bg/60 p-5">
          {loading && (
            <div className="flex h-full min-h-[320px] items-center justify-center">
              <div className="inline-flex items-center gap-3 rounded-2xl border border-line bg-bg/70 px-5 py-4 text-sm font-medium text-content">
                <SpinnerGap size={18} className="animate-spin" />
                Cargando vista previa...
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="flex h-full min-h-[320px] items-center justify-center">
              <div className="max-w-xl rounded-2xl border border-red-800/70 bg-red-950/20 p-6 text-center">
                <p className="text-base font-semibold text-red-200">{error}</p>
              </div>
            </div>
          )}

          {showContent && previewState.type === 'image' && (
            <div className="flex items-center justify-center">
              <img
                src={inlineUrl}
                alt={file.name}
                onError={() => setError(PREVIEW_ERROR_MESSAGE)}
                className="max-h-[75vh] max-w-full rounded-2xl border border-line object-contain"
              />
            </div>
          )}

          {/* Siempre montado: docx-preview necesita el nodo antes de terminar la carga. */}
          <div
            ref={docxContainerRef}
            className={showContent && previewState.type === 'docx'
              ? 'overflow-auto rounded-2xl border border-line bg-white'
              : 'hidden'}
          />

          {showContent && previewState.type === 'sheet' && activeSheet && (
            <div className="flex min-h-[320px] flex-col gap-3">
              {previewState.workbook.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {previewState.workbook.map((sheet, index) => (
                    <button
                      key={sheet.name}
                      type="button"
                      onClick={() => setActiveSheetIndex(index)}
                      className={`rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors ${
                        index === activeSheetIndex
                          ? 'border-amber bg-amber text-on-amber'
                          : 'border-line bg-surface text-content hover:bg-surface-2'
                      }`}
                    >
                      {sheet.name}
                    </button>
                  ))}
                </div>
              )}

              {activeSheet.totalRows > activeSheet.rows.length && (
                <p className="text-xs text-content-muted">
                  {`Mostrando las primeras ${activeSheet.rows.length} de ${activeSheet.totalRows} filas. Descargue el archivo para verlo completo.`}
                </p>
              )}

              {activeSheet.rows.length === 0 ? (
                <p className="text-sm text-content-muted">La hoja está vacía.</p>
              ) : (
                <div className="overflow-auto rounded-2xl border border-line bg-bg/70">
                  <table className="min-w-full border-collapse text-sm text-content">
                    <tbody>
                      {activeSheet.rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className={rowIndex === 0 ? 'bg-table-head font-semibold' : ''}>
                          {Array.from({ length: activeSheet.columnCount }, (_, cellIndex) => (
                            <td key={cellIndex} className="whitespace-nowrap border border-line px-3 py-1.5">
                              {row[cellIndex] ?? ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {showContent && previewState.type === 'text' && (
            <div className="min-h-[320px] overflow-auto rounded-2xl border border-line bg-bg/70 p-5">
              <pre className="whitespace-pre-wrap break-words font-mono text-sm text-content">
                {previewState.text}
              </pre>
            </div>
          )}

          {showContent && previewState.type === 'unsupported' && (
            <div className="flex h-full min-h-[320px] items-center justify-center">
              <div className="max-w-xl rounded-2xl border border-line bg-bg/70 p-6 text-center">
                <FileText size={36} className="mx-auto text-content-muted" />
                <p className="mt-4 text-lg font-semibold text-content">Vista previa no disponible</p>
                <p className="mt-2 text-sm text-content-muted">
                  Este tipo de archivo no se puede previsualizar por ahora. Puede descargarlo desde aquí.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    modalTarget
  );
}

export default FilePreviewModal;
